// Finance domain logic ported 1:1 from the original local portal.

export const CATEGORIES = [
  { name: "Nutrition Consultation", type: "income", bucket: "sales" },
  { name: "Product Sales", type: "income", bucket: "sales" },
  { name: "Brand Collabs", type: "income", bucket: "other_income" },
  { name: "Other Income", type: "income", bucket: "other_income" },
  { name: "Cost of Sales (Meta Ads / Membership)", type: "expense", bucket: "cogs" },
  { name: "Sales & Marketing", type: "expense", bucket: "snm" },
  { name: "Research & Development", type: "expense", bucket: "rnd" },
  { name: "Salary", type: "expense", bucket: "gna" },
  { name: "Office & Admin (fuel, phone, toll, meals)", type: "expense", bucket: "gna" },
  { name: "Company Expenses (Tax)", type: "expense", bucket: "taxes" },
  { name: "Other Expense", type: "expense", bucket: "other_opex" },
];

export const BUCKET_LABEL = {
  sales: "Sales Revenue", cogs: "Cost of Sales", snm: "Sales & Marketing",
  rnd: "Research & Development", gna: "General & Administrative",
  other_opex: "Other Operating Expenses", other_income: "Other Income",
  taxes: "Company Expenses (Tax)",
};

export const EXPENSE_CATEGORIES = CATEGORIES.filter((c) => c.type === "expense");
export const INCOME_CATEGORIES = CATEGORIES.filter((c) => c.type === "income");

export function catInfo(name) {
  return CATEGORIES.find((c) => c.name === name) || { name, type: "expense", bucket: "other_opex" };
}

export function fmtINR(n) {
  const sign = n < 0 ? "-" : "";
  n = Math.abs(Math.round(n || 0));
  return sign + "₹" + n.toLocaleString("en-IN");
}
export function fmtNum(n) { return Math.round(n || 0).toLocaleString("en-IN"); }
export function monthKey(dateStr) { return (dateStr || "").slice(0, 7); }
export function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}
export function addMonths(dateStr, n) {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
}
export function todayStr() { return new Date().toISOString().slice(0, 10); }
export function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
export function formatDMY(dateStr) {
  if (!dateStr) return "-";
  const [y, m, d] = dateStr.split("-");
  return `${parseInt(d)}/${parseInt(m)}/${y}`;
}

export function clientTotals(client) {
  const paid = (client.transactions || []).reduce((a, t) => a + (t.amount || 0), 0);
  const dates = (client.transactions || []).map((t) => t.date).sort();
  const lastDate = dates.length ? dates[dates.length - 1] : "—";
  return { paid, lastDate };
}

export function createSpreadExpenseEntries(category, baseDesc, totalAmount, months, startDate, groupId) {
  const per = totalAmount / months;
  const created = [];
  for (let i = 0; i < months; i++) {
    created.push({
      id: uid(), type: "expense", category, amount: per,
      description: baseDesc + ` (${i + 1}/${months} of ₹${Math.round(totalAmount).toLocaleString("en-IN")})`,
      date: addMonths(startDate, i),
      groupId, groupTotal: totalAmount, groupMonths: months, groupIndex: i + 1, baseDescription: baseDesc,
    });
  }
  return created;
}

export function fyMonthKeys() {
  const keys = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(2026, 3 + i, 1); // FY starts Apr'26
    keys.push(d.toISOString().slice(0, 7));
  }
  return keys;
}

export function overviewSeries(entries) {
  const keys = fyMonthKeys();
  return keys.map((k) => {
    const income = entries.filter((e) => e.type === "income" && monthKey(e.date) === k).reduce((a, e) => a + e.amount, 0);
    const expenses = entries.filter((e) => e.type === "expense" && monthKey(e.date) === k).reduce((a, e) => a + e.amount, 0);
    return { key: k, label: monthLabel(k), income, expenses, net: income - expenses };
  });
}

export function breakdown(entries, type) {
  const map = {};
  entries.filter((e) => e.type === type).forEach((e) => {
    map[e.category] = (map[e.category] || 0) + e.amount;
  });
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

export function snapshot(entries, clients) {
  const thisMonth = monthKey(todayStr());
  const monthEntries = entries.filter((e) => monthKey(e.date) === thisMonth);
  const inc = monthEntries.filter((e) => e.type === "income").reduce((a, e) => a + e.amount, 0);
  const exp = monthEntries.filter((e) => e.type === "expense").reduce((a, e) => a + e.amount, 0);
  const net = inc - exp;
  const totalReceivable = clients.reduce((a, c) => a + (c.balance || 0), 0);
  const clientEarnings = entries.filter((e) => e.type === "income" && catInfo(e.category).bucket === "sales").reduce((a, e) => a + e.amount, 0);
  const collabEarnings = entries.filter((e) => e.type === "income" && catInfo(e.category).bucket === "other_income").reduce((a, e) => a + e.amount, 0);
  return { inc, exp, net, totalReceivable, clientEarnings, collabEarnings, totalEarnings: clientEarnings + collabEarnings };
}

export function periodRange(period) {
  if (period === "month") {
    const k = monthKey(todayStr());
    return { from: k + "-01", to: todayStr(), label: monthLabel(k) };
  }
  if (period === "quarter") {
    const from = addMonths(todayStr(), -2).slice(0, 7) + "-01";
    return { from, to: todayStr(), label: "Last 3 months" };
  }
  if (period === "fy") {
    return { from: "2026-04-01", to: "2027-03-31", label: "FY 2026–27 (Apr '26 – Mar '27)" };
  }
  return { from: "0000-01-01", to: "9999-12-31", label: "All time" };
}

export function computePL(entries, period) {
  const { from, to } = periodRange(period);
  const rows = entries.filter((e) => e.date >= from && e.date <= to);
  const b = { sales: 0, cogs: 0, snm: 0, rnd: 0, gna: 0, other_opex: 0, other_income: 0, taxes: 0 };
  rows.forEach((e) => { const info = catInfo(e.category); b[info.bucket] = (b[info.bucket] || 0) + e.amount; });
  const grossProfit = b.sales - b.cogs;
  const totalOpex = b.snm + b.rnd + b.gna + b.other_opex;
  const netProfitBeforeTax = grossProfit - totalOpex + b.other_income;
  const netProfitAfterTax = netProfitBeforeTax - b.taxes;
  return { b, grossProfit, totalOpex, netProfitBeforeTax, netProfitAfterTax, rowsCount: rows.length };
}

export function nextInvoiceNumber(invoices) {
  const nums = invoices.map((i) => parseInt(String(i.number).replace(/\D/g, "")) || 0);
  const max = nums.length ? Math.max(...nums, 1006) : 1006;
  return String(max + 1);
}

export function computeInvoiceTotals(items) {
  let basis = 0, discount = 0, igst = 0;
  items.forEach((r) => {
    const amt = r.price * r.qty - r.discount;
    basis += r.price * r.qty;
    discount += r.discount;
    igst += amt * (r.igstRate / 100);
  });
  const net = basis - discount;
  return { basis, discount, igst, net, total: net + igst };
}

export function clientsWhatsApp(clients) {
  const rows = clients.slice().sort((a, b) => a.name.localeCompare(b.name));
  const owed = rows.filter((c) => (c.balance || 0) > 0);
  const cleared = rows.filter((c) => !((c.balance || 0) > 0));
  let txt = `*LEAN BY PRIYANKA — CLIENTS*\n_As of ${todayStr()}_\n\n`;
  let totalOwed = 0;
  if (owed.length) {
    txt += `*⚠ BALANCE OWED*\n`;
    owed.forEach((c) => { const { paid } = clientTotals(c); totalOwed += c.balance || 0; txt += `*${c.name}* — Paid: ${fmtINR(paid)} | ${fmtINR(c.balance)} owed\n`; });
    txt += `\n`;
  }
  if (cleared.length) {
    txt += `*✅ CLEARED*\n`;
    cleared.forEach((c) => { const { paid } = clientTotals(c); txt += `*${c.name}* — Paid: ${fmtINR(paid)}\n`; });
    txt += `\n`;
  }
  txt += `*Total balance owed: ${fmtINR(totalOwed)}*`;
  return txt;
}

export function plWhatsApp(entries, period) {
  const { label } = periodRange(period);
  const { b, grossProfit, totalOpex, netProfitBeforeTax, netProfitAfterTax } = computePL(entries, period);
  let txt = `*LEAN BY PRIYANKA — P&L*\n_${label}_\n\n`;
  txt += `*Revenue*\nSales Revenue: ${fmtINR(b.sales)}\nCost of Sales: -${fmtINR(b.cogs)}\n*Gross Profit: ${fmtINR(grossProfit)}*\n\n`;
  txt += `*Operating Expenses*\nSales & Marketing: -${fmtINR(b.snm)}\nR&D: -${fmtINR(b.rnd)}\nG&A: -${fmtINR(b.gna)}\nOther Opex: -${fmtINR(b.other_opex)}\n*Total Opex: -${fmtINR(totalOpex)}*\n\n`;
  txt += `Other Income (Brand Collabs etc.): ${fmtINR(b.other_income)}\n\n`;
  txt += `*NET PROFIT (before tax expenses): ${fmtINR(netProfitBeforeTax)}*\n\n`;
  txt += `Company Expenses (Tax): -${fmtINR(b.taxes)}\n\n`;
  txt += `*NET PROFIT (after tax expenses): ${fmtINR(netProfitAfterTax)}*`;
  return txt;
}

// --- Active clients (plans ending within N months) ---
// Current plan period: the plan that ends last (start = that plan's date, end = start + duration).
export function clientPlanPeriod(client) {
  const plans = (client.transactions || []).filter((t) => t.type === "plan" && t.duration);
  if (!plans.length) return { start: null, end: null };
  let best = null;
  plans.forEach((p) => {
    const e = addMonths(p.date, p.duration);
    if (!best || e > best.end) best = { start: p.date, end: e };
  });
  return best;
}

export function clientPlanEnd(client) {
  return clientPlanPeriod(client).end;
}

export function activeClientsDue(clients, withinMonths = 6) {
  const today = todayStr();
  const limit = addMonths(today, withinMonths);
  const rows = [];
  clients.forEach((c) => {
    const end = clientPlanEnd(c);
    if (end && end >= today && end <= limit) {
      const { paid } = clientTotals(c);
      const daysLeft = Math.round((new Date(end) - new Date(today)) / 86400000);
      rows.push({ ...c, endDate: end, daysLeft, paid });
    }
  });
  return rows.sort((a, b) => a.endDate.localeCompare(b.endDate));
}

export function renewalsWhatsApp(rows) {
  let t = `*LEAN BY PRIYANKA — ACTIVE CLIENTS*\n_Plans ending within 6 months · as of ${todayStr()}_\n\n`;
  if (!rows.length) return t + "No active plans ending in the next 6 months.";
  t += `*Total active clients: ${rows.length}*\n\n`;
  rows.forEach((c) => {
    t += `*${c.name}* — ends ${formatDMY(c.endDate)} (${c.daysLeft} days left)`;
    if (c.balance > 0) t += ` · ${fmtINR(c.balance)} balance`;
    t += "\n";
  });
  return t.trim();
}

// --- Brand collab pipeline ---
export function pipelineAdvanceTotal(p) {
  return (p.advances || []).reduce((a, x) => a + (x.amount || 0), 0);
}

export function pipelineWhatsApp(pipeline) {
  const rows = pipeline.slice().sort((a, b) => (a.expectedDate || "").localeCompare(b.expectedDate || ""));
  let t = `*LEAN BY PRIYANKA — BRAND COLLAB PIPELINE*\n_As of ${todayStr()}_\n\n`;
  if (!rows.length) return t + "Nothing in the pipeline right now.";
  let expected = 0, advance = 0, due = 0;
  rows.forEach((p) => {
    const exp = p.expectedAmount || 0, paid = pipelineAdvanceTotal(p), bal = Math.max(exp - paid, 0);
    expected += exp; advance += paid; due += bal;
    t += `*${p.brand}* — ${fmtINR(exp)}`;
    if (paid > 0) t += ` | Advance: ${fmtINR(paid)} | Balance: ${fmtINR(bal)}`;
    t += `\n${p.status || "Pipeline"}${p.expectedDate ? ` · expected ${formatDMY(p.expectedDate)}` : ""}`;
    if (p.notes) t += ` · ${p.notes}`;
    t += `\n\n`;
  });
  t += `*Total expected: ${fmtINR(expected)}*`;
  if (advance > 0) t += `\n*Advances received: ${fmtINR(advance)}*\n*Balance still due: ${fmtINR(due)}*`;
  return t;
}
